/**
 * Filters bar for the redesigned Analytics dashboard.
 *
 * - Branch picker: dropdown listing the user's branches (`All branches`
 *   when none selected).
 * - Date range: preset list (7d / 30d / 90d / YTD / Custom). Custom
 *   reveals two inline date inputs.
 * - Compare segment: turns the previous-period comparison on or off.
 * - Export CSV: existing PDF endpoint reused with `?format=csv` (TODO
 *   when backend supports it; stub for now).
 * - Insights: AI summary stub — disabled "Coming soon".
 */
import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";

import { Card } from "../../../../UI";
import {
  DATE_RANGE_PRESETS,
  resolveDateRange
} from "../../../../../constants/analytics.constants";
import { DashboardFiltersStyles } from "./dashboardFilters.styles";

const ChevronIcon = () => (
  <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
    <path
      d="M1 1l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BranchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M8 4.7a2.34 2.34 0 01-2.34-2.34A2.35 2.35 0 018 .02a2.35 2.35 0 012.34 2.34A2.35 2.35 0 018 4.7zM.31 13.41a2.34 2.34 0 113.21.86 2.34 2.34 0 01-3.21-.86zm12.18.85a2.34 2.34 0 112.34-4.06 2.34 2.34 0 01-2.34 4.06z" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
    <path d="M11.7 1.3h-.6V.4a.4.4 0 10-.9 0v.9H4v-.9a.4.4 0 10-1 0v.9h-.6c-.6 0-1 .4-1 1v9.5c0 .5.4.9 1 .9h9.4c.5 0 1-.4 1-.9V2.3c0-.6-.5-1-1-1zm0 10.5H2.3V5h9.4v6.8z" />
  </svg>
);

// SparkIcon — used by the (currently hidden) Insights button. Kept here
// so we can drop the button back in by un-commenting it below without
// having to re-author the icon.
// eslint-disable-next-line no-unused-vars
const SparkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M7 1v3M7 10v3M1 7h3M10 7h3M3 3l2 2M11 11L9 9M11 3L9 5M3 11l2-2"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  </svg>
);

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M7 1v8m0 0L4 6m3 3l3-3M2 11v1.5h10V11"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const onClick = e => {
      if (ref.current && !ref.current.contains(e.target)) handler();
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [ref, handler]);
};

const DashboardFilters = ({
  branchId,
  onBranchChange,
  rangePreset,
  onRangePresetChange,
  customRange,
  onCustomRangeChange,
  compare,
  onCompareChange,
  onExport,
  exporting = false
}) => {
  const branches = useSelector(state => state.app.branches || []);
  const [openBranch, setOpenBranch] = useState(false);
  const [openRange, setOpenRange] = useState(false);
  const branchRef = useRef();
  const rangeRef = useRef();
  useClickOutside(branchRef, () => setOpenBranch(false));
  useClickOutside(rangeRef, () => setOpenRange(false));

  const branchLabel = branchId
    ? branches.find(b => b.id === branchId)?.name || "Branch"
    : "All branches";
  const rangeLabel =
    DATE_RANGE_PRESETS.find(p => p.id === rangePreset)?.label || "Range";

  return (
    <DashboardFiltersStyles>
      <Card className="filters-card">
        <div className="filters-left">
          <div className="field" ref={branchRef}>
            <span className="field-label">Branch</span>
            <button
              type="button"
              className="select"
              onClick={() => setOpenBranch(o => !o)}
              aria-expanded={openBranch}
            >
              <span className="select-icon"><BranchIcon /></span>
              <span className="select-trigger">
                <span>{branchLabel}</span>
                <span className="select-caret"><ChevronIcon /></span>
              </span>
            </button>
            {openBranch && (
              <div className="popover">
                <div
                  className={`popover-opt ${!branchId ? "is-active" : ""}`}
                  onClick={() => {
                    onBranchChange(null);
                    setOpenBranch(false);
                  }}
                >
                  <BranchIcon /> All branches
                </div>
                {branches.map(b => (
                  <div
                    key={b.id}
                    className={`popover-opt ${b.id === branchId ? "is-active" : ""}`}
                    onClick={() => {
                      onBranchChange(b.id);
                      setOpenBranch(false);
                    }}
                  >
                    <BranchIcon /> {b.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="field" ref={rangeRef}>
            <span className="field-label">Date range</span>
            <button
              type="button"
              className="select"
              onClick={() => setOpenRange(o => !o)}
              aria-expanded={openRange}
            >
              <span className="select-icon"><CalendarIcon /></span>
              <span className="select-trigger">
                <span>{rangeLabel}</span>
                <span className="select-caret"><ChevronIcon /></span>
              </span>
            </button>
            {openRange && (
              <div className="popover">
                {DATE_RANGE_PRESETS.map(p => (
                  <div
                    key={p.id}
                    className={`popover-opt ${p.id === rangePreset ? "is-active" : ""}`}
                    onClick={() => {
                      onRangePresetChange(p.id);
                      setOpenRange(false);
                    }}
                  >
                    <CalendarIcon /> {p.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {rangePreset === "custom" && (
            <div className="custom-range">
              <div className="field">
                <span className="field-label">From</span>
                <input
                  type="date"
                  className="date-input"
                  value={customRange.dateFrom || ""}
                  onChange={e =>
                    onCustomRangeChange({
                      ...customRange,
                      dateFrom: e.target.value
                    })
                  }
                />
              </div>
              <div className="field">
                <span className="field-label">To</span>
                <input
                  type="date"
                  className="date-input"
                  value={customRange.dateTo || ""}
                  onChange={e =>
                    onCustomRangeChange({
                      ...customRange,
                      dateTo: e.target.value
                    })
                  }
                />
              </div>
            </div>
          )}

          <div className="field">
            <span className="field-label">Compare</span>
            <div className="seg" role="group" aria-label="Compare with previous period">
              <button
                type="button"
                className={`seg-btn ${!compare ? "is-active" : ""}`}
                onClick={() => onCompareChange(false)}
              >
                Off
              </button>
              <button
                type="button"
                className={`seg-btn ${compare ? "is-active" : ""}`}
                onClick={() => onCompareChange(true)}
              >
                Previous period
              </button>
            </div>
          </div>
        </div>

        <div className="filters-right">
          <button
            type="button"
            className="btn btn-sm"
            onClick={onExport}
            disabled={exporting}
          >
            <DownloadIcon /> {exporting ? "Exporting…" : "Export"}
          </button>
          {/*
            Insights button (AI summary) — temporarily hidden until we
            decide whether it ships as a static heuristic or a real
            Claude API integration. Restore by uncommenting + removing
            the unused-vars eslint-disable on SparkIcon below.
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled
              title="AI-generated insights coming soon"
            >
              <SparkIcon /> Insights
            </button>
          */}
        </div>
      </Card>
    </DashboardFiltersStyles>
  );
};

// Re-export the resolver helper for the Analytics page convenience.
export { resolveDateRange };
export default DashboardFilters;
